import React, { useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import Backend from "../utils/api.js";
import pinpointLogo from "../assets/pinpoint_no_bg.svg";
import UserCard from "./Components/UserCard.jsx";
import {
  Box,
  Flex,
  Grid,
  Image,
  Text,
} from "@chakra-ui/react";
import { useDocumentTitle } from "../utils/useDocumentTitle";

function LabStatus({ currLab }) {
  const [userCount, setUserCount] = useState(0);
  const [userList, setUserList] = useState([]);
  const currLabName = (currLab && currLab.name) || "Loading";

  useDocumentTitle("Space status – Pinpoint");

  async function getUserMachines(user) {
    const res = await Backend.getActiveMachinesByUserForLab(
      user.UID,
      currLab.id
    );
    if (res.error || !res.items) return [];
    return res.items.map((m) => m.machineName).filter(Boolean);
  }

  useEffect(() => {
    if (currLab.id === undefined) return;

    const fetchData = async () => {
      const data = await Backend.getActiveUsersByLab(currLab.id, 1000);
      if (data.error || !data.items) return;
      const activeUsers = data.items;

      const promises = activeUsers.map(async (user) => {
        const userMachines = await getUserMachines(user);
        return [user, userMachines, new Date(user.swipe_in_time)];
      });

      const userListBuilder = await Promise.all(promises);
      setUserCount(activeUsers.length);
      setUserList(userListBuilder);
    };

    fetchData();

    const es = Backend.subscribeToSpaceEvents(currLab.id, (event) => {
      if (
        event.event === 'session_started' ||
        event.event === 'session_ended' ||
        event.event === 'session_updated' ||
        event.event === 'space_snapshot'
      ) {
        fetchData();
      }
    });

    return () => es.close();
  }, [currLab.id]);

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Header */}
      <Flex
        bg="white"
        borderBottomWidth="1px"
        borderColor="gray.100"
        px={{ base: 4, md: 8, xl: 12 }}
        py={{ base: 3, md: 4, xl: 5 }}
        align="center"
        sx={{
          "@media (min-width: 120em)": { px: "80px", py: "28px" },
        }}
      >
        <Image
          boxSize={{ base: 7, md: 8, xl: 10 }}
          src={pinpointLogo}
          alt="Pinpoint logo"
          mr={{ base: 2, md: 3, xl: 4 }}
          sx={{
            "@media (min-width: 120em)": { width: "56px", height: "56px", mr: "20px" },
          }}
        />
        <Text
          fontSize={{ base: "xl", md: "2xl", xl: "3xl" }}
          fontWeight={700}
          color="gray.800"
          flex={1}
          sx={{
            "@media (min-width: 120em)": { fontSize: "42px" },
          }}
        >
          {currLabName}
        </Text>
        <Flex
          align="center"
          bg={userCount > 0 ? "green.50" : "gray.100"}
          rounded="full"
          px={{ base: 2.5, md: 3, xl: 4 }}
          py={{ base: 1, md: 1.5 }}
          sx={{
            "@media (min-width: 120em)": { px: "24px", py: "10px" },
          }}
        >
          <Box
            w={{ base: 2, xl: 2.5 }}
            h={{ base: 2, xl: 2.5 }}
            rounded="full"
            bg={userCount > 0 ? "green.400" : "gray.300"}
            mr={{ base: 1.5, md: 2 }}
            sx={{
              "@media (min-width: 120em)": { width: "14px", height: "14px", mr: "12px" },
            }}
          />
          <Text
            fontSize={{ base: "xs", md: "sm", xl: "md" }}
            fontWeight={600}
            color={userCount > 0 ? "green.700" : "gray.500"}
            sx={{
              "@media (min-width: 120em)": { fontSize: "22px" },
            }}
          >
            {userCount} active
          </Text>
        </Flex>
      </Flex>

      {/* Card grid */}
      <Box
        px={{ base: 4, md: 8, xl: 12 }}
        py={{ base: 4, md: 6, xl: 8 }}
        sx={{
          "@media (min-width: 120em)": { px: "80px", py: "48px" },
        }}
      >
        {userList.length > 0 ? (
          <Grid
            templateColumns={{
              base: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              xl: "repeat(4, 1fr)",
              "2xl": "repeat(5, 1fr)",
            }}
            sx={{
              "@media (min-width: 120em)": {
                gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
              },
            }}
            gap={{ base: 3, md: 4, xl: 5 }}
          >
            {userList.map((entry) => {
              const [user, machines, swipeInTime] = entry;
              return (
                <UserCard
                  key={user.UID}
                  user={user}
                  machines={machines}
                  swipeInTime={swipeInTime}
                />
              );
            })}
          </Grid>
        ) : (
          <Flex
            direction="column"
            align="center"
            justify="center"
            minH="70vh"
          >
            <Text
              fontSize={{ base: "lg", xl: "xl" }}
              fontWeight={500}
              color="gray.400"
              sx={{
                "@media (min-width: 120em)": { fontSize: "36px" },
              }}
            >
              No active users
            </Text>
          </Flex>
        )}
      </Box>
    </Box>
  );
}

export default withRouter(LabStatus);
